import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AppBanner, { useAppBanner } from "../components/getapp/AppBanner";
import HeroGetAppChip from "../components/getapp/HeroGetAppChip";
import HeroDownloadButton from "../components/getapp/HeroDownloadButton";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import PromptBox from "../components/PromptBox";
import SkyBackdrop from "../components/brand/SkyBackdrop";
import WorkspaceMockup from "../components/landing/WorkspaceMockup";
import HowItWorks from "../components/landing/HowItWorks";
import Features from "../components/landing/Features";
import Pricing from "../components/landing/Pricing";
import { useAuth } from "../context/AuthContext";
import { useAuthModal, PENDING_KEY } from "../context/AuthModalContext";
import { api, errMsg } from "../lib/api";

const Hero = ({ onSubmit, busy }) => (
  <section className="relative pt-28 sm:pt-36 pb-20 overflow-hidden">
    <SkyBackdrop />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto text-center fade-up">
        <div className="flex flex-wrap items-center justify-center gap-2">
          <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/70 backdrop-blur ring-1 ring-white text-xs font-semibold text-sky-700" data-testid="hero-badge">
            <span className="w-1.5 h-1.5 rounded-full bg-sky-500 animate-pulse" /> AI app builder
          </span>
          <HeroGetAppChip />
        </div>
        <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] text-slate-900">
          Turn your idea into a <span className="text-sky-600">live app</span> — just by chatting.
        </h1>
        <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">
          Describe what you want. Ishukart writes the code, shows a live preview, takes your feedback and publishes it in one click.
        </p>
      </div>
      <div className="mt-10 max-w-3xl mx-auto fade-up" style={{ animationDelay: "120ms" }}>
        <PromptBox onSubmit={onSubmit} loading={busy} testidPrefix="hero" allowAttach={false} />
        <div className="mt-8 flex justify-center"><HeroDownloadButton /></div>
      </div>
      <div className="mt-20 fade-up" style={{ animationDelay: "240ms" }}>
        <WorkspaceMockup />
      </div>
    </div>
  </section>
);

const FinalCta = ({ onStart }) => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-28">
    <div className="relative rounded-[32px] bg-gradient-to-br from-sky-300 via-sky-200 to-white p-10 sm:p-14 overflow-hidden ring-1 ring-sky-200">
      <div className="absolute -right-20 -top-10 w-[480px] h-48 rounded-full bg-white/70 blur-3xl" />
      <h2 className="relative text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 max-w-xl">Your next app is one message away.</h2>
      <p className="relative mt-3 text-slate-600 max-w-lg">Create your account, pick a credit pack and build your first app today.</p>
      <button onClick={onStart} data-testid="final-cta-btn" className="relative mt-8 h-12 px-7 rounded-full bg-slate-900 text-white font-semibold hover:bg-slate-700 transition-colors">
        Start building
      </button>
    </div>
  </section>
);

export default function Landing() {
  const { user } = useAuth();
  const { openSignup } = useAuthModal();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const onSubmit = async (prompt, model, opts = {}) => {
    if (!user) {
      sessionStorage.setItem(PENDING_KEY, JSON.stringify({ prompt, model, opts }));
      toast("Create an account to start building", { description: "Your idea is saved — we'll build it right after." });
      return openSignup();
    }
    setBusy(true);
    try {
      const r = await api.createProject(prompt, model, opts);
      navigate(`/project/${r.project.id}`);
    } catch (e) {
      toast.error(errMsg(e, "Could not start the build"));
    } finally {
      setBusy(false);
    }
  };

  const onBuy = () => (user ? navigate("/billing") : openSignup());
  const appBanner = useAppBanner();

  // Opening the installed app while signed in goes straight to the projects.
  useEffect(() => {
    if (user && /[?&]source=(app|android|ios)\b/.test(window.location.search)) navigate("/home", { replace: true });
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-[#F5F8FC] text-slate-700" data-testid="landing-page">
      <SiteNav banner={appBanner.show && <AppBanner onClose={appBanner.close} />} />
      {appBanner.show && <div className="h-14" aria-hidden="true" />}
      <Hero onSubmit={onSubmit} busy={busy} />
      <HowItWorks />
      <Features />
      <Pricing onBuy={onBuy} />
      <FinalCta onStart={() => (user ? navigate("/home") : openSignup())} />
      <SiteFooter />
    </div>
  );
}
